import { useMemo, useState } from 'react';
import { Sun, Moon, Desktop, Rows, List, CircleNotch, CaretDown } from '@phosphor-icons/react';

import type { ManifestSection, SettingsManifest } from '@/lib/settingsApi';
import { usePreferences, useUpdatePreferences } from '@/lib/preferences';
import { displayPreferencesOf, formatDate, formatDateTime, formatNumber, usePracticeTimezone } from '@/lib/format';
import { useThemeStore, type ThemeOption } from '@/lib/themeStore';
import { useSidebarStore } from '@/components/layout/sidebarStore';
import { listTimeZoneOptions } from '@/lib/timezone';
import { FieldRow, Panel, SaveIndicator, SectionIntro, useSaveState, useSettingLookup } from './primitives';

/**
 * Appearance & Regional.
 *
 * Every control here writes a PERSONAL preference and nothing else. None of them touches the
 * chambers: a barrister choosing to read times in another timezone changes what they SEE, never
 * what a listing MEANS, and the practice's own timezone is shown beside it read-only so the
 * distinction is visible rather than merely documented.
 *
 * The live preview matters. These settings were stored and consumed by nothing, so the only way
 * to know whether a choice had taken effect was to go and look somewhere else. The preview
 * answers that on the spot, with the same formatter the rest of the product uses.
 */

const DATE_FORMATS = [
  { value: 'dd/MM/yyyy', label: 'Day first' },
  { value: 'MM/dd/yyyy', label: 'Month first' },
  { value: 'yyyy-MM-dd', label: 'Year first' },
] as const;

const TIME_FORMATS = [
  { value: '24h', label: '24-hour' },
  { value: '12h', label: '12-hour' },
] as const;

const LOCALES = [
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-IE', label: 'English (Ireland)' },
  { value: 'cy-GB', label: 'Welsh (United Kingdom)' },
] as const;

/** Where a person may choose to start. Only paths their own role can enter are offered. */
const LANDING_PAGES: Record<string, Array<{ value: string; label: string }>> = {
  barrister: [
    { value: '/barrister', label: 'Dashboard' },
    { value: '/barrister/matters', label: 'My Matters' },
    { value: '/barrister/diary', label: 'My Diary' },
    { value: '/barrister/inbox', label: 'Inbox' },
    { value: '/barrister/hearings', label: 'Hearings' },
  ],
  clerk: [
    { value: '/clerk', label: 'Dashboard' },
    { value: '/clerk/diary', label: 'Diary' },
    { value: '/clerk/matters', label: 'Matters' },
    { value: '/clerk/hearings', label: 'Hearings' },
  ],
  admin: [
    { value: '/admin', label: 'Dashboard' },
    { value: '/admin/matters', label: 'Matters' },
    { value: '/admin/hearings', label: 'Hearings' },
    { value: '/admin/reports', label: 'Reports' },
  ],
  staff: [{ value: '/settings', label: 'Settings' }],
};

function Choice<T extends string>({
  value,
  options,
  onChange,
  busy,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string; Icon?: React.ElementType }>;
  onChange: (v: T) => void;
  busy?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={busy}
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition disabled:opacity-50
            ${value === o.value
              ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300'
              : 'border-border text-foreground hover:bg-muted'}`}
        >
          {o.Icon && <o.Icon className="h-3.5 w-3.5" />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Select({
  value,
  onChange,
  children,
  busy,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  busy?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={busy}
      onChange={(e) => onChange(e.target.value)}
      className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50"
    >
      {children}
    </select>
  );
}

export function AppearanceSection({ section, manifest }: { section: ManifestSection; manifest: SettingsManifest }) {
  const setting = useSettingLookup(section);
  const prefs = usePreferences();
  const save = useUpdatePreferences();
  const practiceTimezone = usePracticeTimezone();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  // The sidebar has ONE writer: the store, which moves the shell immediately and persists to the
  // account itself. Writing the preference directly from here would save the value and leave the
  // sidebar where it was — the exact "saved, not applied" state this phase removes.
  const sidebarCollapsed = useSidebarStore((st) => st.collapsed);
  const setSidebarCollapsed = useSidebarStore((st) => st.setCollapsed);
  const { state, error, run } = useSaveState();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /**
   * The two long panels start shut, same arrow as Security and Notifications. "Appearance"
   * stays open: it is two rows of buttons and the thing most people came here to change.
   */
  const [openFormats, setOpenFormats] = useState(false);
  const [openLanding, setOpenLanding] = useState(false);

  const display = displayPreferencesOf(prefs);
  // Computed once per render of this section rather than per keystroke — the full IANA list.
  const timezoneOptions = useMemo(() => listTimeZoneOptions(), []);
  const landingOptions = LANDING_PAGES[manifest.context.actor] ?? LANDING_PAGES['barrister']!;

  const change = (key: string, patch: Parameters<typeof save>[0]) => {
    setBusyKey(key);
    void run(() => save(patch)).finally(() => setBusyKey(null));
  };

  // The same instant, rendered with whatever is currently chosen.
  const sample = new Date();

  return (
    <div className="space-y-5">
      <SectionIntro title="Appearance & Regional" description="How the platform looks to you, and how dates, times and numbers are written" />

      <Panel title="Appearance" description="Applies wherever you sign in, not just this browser">
        <FieldRow setting={setting('appearance.theme')} readOnlyValue={theme}>
          <Choice<ThemeOption>
            value={theme}
            onChange={(v) => setTheme(v)}
            options={[
              { value: 'light', label: 'Light', Icon: Sun },
              { value: 'dark', label: 'Dark', Icon: Moon },
              { value: 'system', label: 'Match my device', Icon: Desktop },
            ]}
          />
        </FieldRow>

        <FieldRow
          setting={setting('appearance.density')}
          hint="How much fits on a screen. Tables and lists tighten up."
          readOnlyValue={prefs.density}
        >
          <Choice
            value={prefs.density}
            busy={busyKey === 'density'}
            onChange={(v) => change('density', { density: v })}
            options={[
              { value: 'comfortable' as const, label: 'Comfortable', Icon: Rows },
              { value: 'compact' as const, label: 'Compact', Icon: List },
            ]}
          />
        </FieldRow>

        <FieldRow
          setting={setting('appearance.sidebar')}
          hint="Follows you between machines"
          readOnlyValue={sidebarCollapsed ? 'Collapsed' : 'Expanded'}
        >
          <Choice
            value={sidebarCollapsed ? 'collapsed' : 'expanded'}
            onChange={(v) => setSidebarCollapsed(v === 'collapsed')}
            options={[
              { value: 'expanded' as const, label: 'Expanded' },
              { value: 'collapsed' as const, label: 'Collapsed' },
            ]}
          />
        </FieldRow>
      </Panel>

      <Panel
        title="Dates, times and numbers"
        description="How they are written for you. The times themselves do not change."
        action={
          <button
            type="button"
            onClick={() => setOpenFormats((v) => !v)}
            aria-expanded={openFormats}
            aria-label={openFormats ? 'Hide date and number formats' : 'Show date and number formats'}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
          >
            <CaretDown size={12} weight="bold" className={`transition-transform ${openFormats ? 'rotate-180' : ''}`} />
          </button>
        }
      >
        {openFormats && <>
        <FieldRow
          setting={setting('appearance.timezone')}
          hint="The clock your times are shown on"
          readOnlyValue={prefs.effective.timezone}
        >
          <div>
            <Select
              value={prefs.timezone ?? ''}
              busy={busyKey === 'timezone'}
              onChange={(v) => change('timezone', { timezone: v === '' ? null : v })}
            >
              <option value="">Follow the practice ({practiceTimezone})</option>
              {timezoneOptions.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label} — {tz.sub}</option>
              ))}
            </Select>
            {/* The boundary, said out loud where the choice is made. */}
            <p className="mt-1.5 text-xs text-muted-foreground">
              This changes how times are shown <em>to you</em>. Your chambers operates in{' '}
              <strong className="font-medium text-foreground">{practiceTimezone}</strong>, and a
              hearing keeps the time it was listed for whoever is looking at it.
            </p>
          </div>
        </FieldRow>

        <FieldRow
          setting={setting('appearance.dateFormat')}
          hint="How dates are written where they are shown as numbers. Dates written out in full follow your language."
          readOnlyValue={prefs.dateFormat}
        >
          <Choice
            value={prefs.dateFormat as (typeof DATE_FORMATS)[number]['value']}
            busy={busyKey === 'dateFormat'}
            onChange={(v) => change('dateFormat', { dateFormat: v })}
            options={DATE_FORMATS.map((f) => ({
              value: f.value,
              label: `${f.label} — ${formatDate(sample, { ...display, dateFormat: f.value })}`,
            }))}
          />
        </FieldRow>

        <FieldRow setting={setting('appearance.timeFormat')} readOnlyValue={prefs.timeFormat}>
          <Choice
            value={prefs.timeFormat as (typeof TIME_FORMATS)[number]['value']}
            busy={busyKey === 'timeFormat'}
            onChange={(v) => change('timeFormat', { timeFormat: v })}
            options={TIME_FORMATS.map((f) => ({ value: f.value, label: f.label }))}
          />
        </FieldRow>

        <FieldRow
          setting={setting('appearance.locale')}
          hint="Decides how numbers and month names are written"
          readOnlyValue={prefs.effective.locale}
        >
          <div>
            <Select
              value={prefs.locale ?? ''}
              busy={busyKey === 'locale'}
              onChange={(v) => change('locale', { locale: v === '' ? null : v })}
            >
              <option value="">Follow the practice ({prefs.effective.localeInherited ? prefs.effective.locale : 'en-GB'})</option>
              {LOCALES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </Select>
            {/* Say what this does NOT do, rather than let someone infer a translated product. */}
            <p className="mt-1.5 text-xs text-muted-foreground">
              This sets number and date formatting. The platform’s own wording is English
              throughout — it is not a translation setting.
            </p>
          </div>
        </FieldRow>

        {/* The proof, on the spot: the same formatter every other screen uses. */}
        <div className="mt-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">How this looks</div>
          <dl className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-4 sm:block">
              <dt className="text-xs text-muted-foreground">A date and time</dt>
              <dd className="text-foreground" data-testid="preview-datetime">{formatDateTime(sample, display)}</dd>
            </div>
            <div className="flex justify-between gap-4 sm:block">
              <dt className="text-xs text-muted-foreground">A number</dt>
              <dd className="text-foreground" data-testid="preview-number">{formatNumber(1234567.89, display)}</dd>
            </div>
          </dl>
        </div>
        </>}
      </Panel>

      <Panel
        title="Where you start"
        description="The page that opens after you sign in"
        action={
          <button
            type="button"
            onClick={() => setOpenLanding((v) => !v)}
            aria-expanded={openLanding}
            aria-label={openLanding ? 'Hide landing page choice' : 'Show landing page choice'}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
          >
            <CaretDown size={12} weight="bold" className={`transition-transform ${openLanding ? 'rotate-180' : ''}`} />
          </button>
        }
      >
        {openLanding && <FieldRow
          setting={setting('appearance.landingPage')}
          hint="Yours alone — it does not change where anyone else lands"
          readOnlyValue={prefs.landingPage ?? 'Dashboard'}
        >
          <Select
            value={prefs.landingPage ?? ''}
            busy={busyKey === 'landingPage'}
            onChange={(v) => change('landingPage', { landingPage: v === '' ? null : v })}
          >
            <option value="">My dashboard (default)</option>
            {landingOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </FieldRow>}
      </Panel>

      <div className="flex items-center gap-2">
        {busyKey && <CircleNotch className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        <SaveIndicator state={state} error={error} />
      </div>
    </div>
  );
}
